import React from "react";

/**
 * Primitivas visuales para las mini-maquetas ("qué cambia en pantalla") de las
 * pantallas de configuración. No reciben datos reales ni disparan acciones, y
 * sólo se montan al abrir el panel "Ver detalle".
 */

const NUEVO = "#00BFD6";
const OCULTO = "#ff7875";
const MENU_AZUL = "#001529";
const MENU_ICONO = "#00BFD6";

const colorDe = (estado) => (estado === "oculto" ? OCULTO : NUEVO);

/** Marco que simula un recorte de pantalla del sistema. */
export const MockFrame = ({ title, children, style }) => (
    <div
        style={{
            border: "1px solid #e4e4e4",
            borderRadius: 6,
            background: "#fff",
            overflow: "hidden",
            ...style,
        }}
    >
        {title && (
            <div
                style={{
                    padding: "4px 8px",
                    background: "#fafafa",
                    borderBottom: "1px solid #eee",
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: "#9a9a9a",
                }}
            >
                {title}
            </div>
        )}
        <div style={{ padding: 8, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
            {children}
        </div>
    </div>
);

/** Etiqueta "aparece" / "se oculta" que acompaña al elemento maquetado. */
export const MockTag = ({ estado }) => {
    const esOculto = estado === "oculto";
    const color = colorDe(estado);
    return (
        <span
            style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                color,
                border: `1px solid ${color}66`,
                background: `${color}12`,
                borderRadius: 3,
                padding: "0 4px",
                lineHeight: "14px",
                whiteSpace: "nowrap",
                flexShrink: 0,
            }}
        >
            {esOculto ? "se oculta" : "aparece"}
        </span>
    );
};

/**
 * Botón falso.
 * @param {'nuevo'|'oculto'} estado - marca si aparece o si se oculta
 */
export const MockButton = ({ children, estado }) => {
    const esOculto = estado === "oculto";
    const color = colorDe(estado);
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span
                style={{
                    display: "inline-block",
                    fontSize: 11,
                    lineHeight: "20px",
                    padding: "0 8px",
                    borderRadius: 4,
                    color: estado ? color : "#666",
                    background: esOculto ? "#fafafa" : `${color}14`,
                    border: `1px ${esOculto ? "dashed" : "solid"} ${estado ? color : "#d9d9d9"}`,
                    textDecoration: esOculto ? "line-through" : "none",
                    opacity: esOculto ? 0.8 : 1,
                    whiteSpace: "nowrap",
                }}
            >
                {children}
            </span>
            {estado && <MockTag estado={estado} />}
        </span>
    );
};

/** Campo falso: etiqueta arriba, caja con el valor abajo. */
export const MockField = ({ label, value, estado, width = 120 }) => {
    const esOculto = estado === "oculto";
    const color = colorDe(estado);
    return (
        <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 5 }}>
            <span style={{ display: "inline-block" }}>
                <span style={{ display: "block", fontSize: 9.5, color: "#999", marginBottom: 2 }}>
                    {label}
                </span>
                <span
                    style={{
                        display: "block",
                        minWidth: width,
                        fontSize: 11,
                        lineHeight: "20px",
                        padding: "0 6px",
                        borderRadius: 4,
                        background: "#fff",
                        color: estado && !esOculto ? "#333" : "#aaa",
                        border: `1px ${esOculto ? "dashed" : "solid"} ${estado ? color : "#d9d9d9"}`,
                        textDecoration: esOculto ? "line-through" : "none",
                        whiteSpace: "nowrap",
                    }}
                >
                    {value || " "}
                </span>
            </span>
            {estado && <MockTag estado={estado} />}
        </span>
    );
};

/**
 * Renglón de menú falso.
 * @param {'nuevo'|'oculto'} estado - marca si aparece o si se oculta
 * @param {boolean} oscuro - úsalo cuando la maqueta es el menú lateral del sistema
 * @param {node} icon - el mismo icono que el módulo lleva en el menú
 */
export const MockMenuItem = ({ children, estado, oscuro, icon }) => {
    const esOculto = estado === "oculto";
    const color = colorDe(estado);
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    lineHeight: "22px",
                    padding: "0 8px",
                    borderRadius: 4,
                    background: oscuro
                        ? MENU_AZUL
                        : esOculto ? "#fafafa" : `${color}14`,
                    color: oscuro
                        ? (esOculto ? "#8fa3b0" : "#fff")
                        : esOculto ? "#999" : "#00909e",
                    borderLeft: `2px solid ${color}`,
                    textDecoration: esOculto ? "line-through" : "none",
                    opacity: oscuro && esOculto ? 0.65 : 1,
                    whiteSpace: "nowrap",
                }}
            >
                {icon && (
                    <span style={{ color: MENU_ICONO, display: "inline-flex", fontSize: 11 }}>
                        {icon}
                    </span>
                )}
                {children}
            </span>
            {estado && <MockTag estado={estado} />}
        </span>
    );
};

/**
 * Tabla falsa, para columnas que se agregan o se quitan.
 * @param {Array} columns - [{ label, estado?: 'nuevo'|'oculto', sample? }]
 * @param {number} rows - renglones de relleno
 */
export const MockTable = ({ columns = [], rows = 2 }) => (
    <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 10, minWidth: "100%" }}>
            <thead>
                <tr>
                    {columns.map((col, i) => {
                        const esOculto = col.estado === "oculto";
                        const color = colorDe(col.estado);
                        return (
                            <th
                                key={i}
                                style={{
                                    textAlign: "left",
                                    padding: "3px 6px",
                                    fontWeight: 700,
                                    color: col.estado ? color : "#999",
                                    background: col.estado ? `${color}12` : "#fafafa",
                                    border: `1px ${esOculto ? "dashed" : "solid"} ${col.estado ? `${color}55` : "#eee"}`,
                                    textDecoration: esOculto ? "line-through" : "none",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {col.label}
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: rows }).map((_, r) => (
                    <tr key={r}>
                        {columns.map((col, i) => (
                            <td
                                key={i}
                                style={{
                                    padding: "3px 6px",
                                    border: "1px solid #f2f2f2",
                                    color: "#c9c9c9",
                                    background: col.estado === "oculto" ? "#fcfcfc" : "#fff",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {col.sample || "———"}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

/** Comparación desactivado / activado, para opciones que cambian un texto o un cálculo. */
export const MockDiff = ({
    antes,
    despues,
    labelAntes = "Desactivado",
    labelDespues = "Activado",
}) => {
    const linea = (label, contenido, activo) => (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "2px 0" }}>
            <span
                style={{
                    flex: "0 0 68px",
                    fontSize: 9,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                    color: activo ? NUEVO : "#bbb",
                    paddingTop: 4,
                }}
            >
                {label}
            </span>
            <span
                style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 11,
                    lineHeight: 1.45,
                    color: activo ? "#333" : "#999",
                    background: activo ? `${NUEVO}0f` : "#fafafa",
                    border: `1px solid ${activo ? `${NUEVO}44` : "#eee"}`,
                    borderRadius: 4,
                    padding: "3px 6px",
                    wordBreak: "break-word",
                }}
            >
                {contenido}
            </span>
        </div>
    );
    return (
        <div style={{ width: "100%" }}>
            {linea(labelAntes, antes, false)}
            {linea(labelDespues, despues, true)}
        </div>
    );
};

/* ───────────── Referencias en línea dentro del texto de una opción ────────
 *
 * No dibujan una pantalla: marcan una palabra dentro de una frase. `resumen`,
 * `detalle` y `aviso` aceptan nodo además de texto, y el buscador los sigue
 * indexando porque recorre el contenido, no sólo las cadenas.
 */

const baseRef = {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11.5,
    fontWeight: 600,
    lineHeight: "18px",
    padding: "0 6px",
    borderRadius: 4,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
};

/**
 * Módulo o pantalla del menú principal, con los colores que tiene allá.
 * @param {node} icon - opcional, el mismo icono que el módulo usa en el menú
 */
export const RefModulo = ({ children, icon }) => (
    <span style={{ ...baseRef, background: MENU_AZUL, color: "#fff" }}>
        {icon && (
            <span style={{ color: MENU_ICONO, display: "inline-flex", fontSize: 11 }}>
                {icon}
            </span>
        )}
        {children}
    </span>
);

/** Botón del sistema nombrado dentro de una frase. */
export const RefBoton = ({ children, icon }) => (
    <span
        style={{
            ...baseRef,
            background: "#fff",
            color: MENU_AZUL,
            border: `1px solid ${MENU_AZUL}`,
        }}
    >
        {icon && <span style={{ display: "inline-flex", fontSize: 11 }}>{icon}</span>}
        {children}
    </span>
);

/** Campo, columna o etiqueta concreta. Más discreto que los dos anteriores. */
export const RefCampo = ({ children }) => (
    <span
        style={{
            ...baseRef,
            fontWeight: 500,
            background: `${MENU_AZUL}0a`,
            color: MENU_AZUL,
            border: `1px solid ${MENU_AZUL}26`,
        }}
    >
        {children}
    </span>
);

const ConfigMock = {
    MockFrame, MockTag, MockButton, MockField, MockMenuItem, MockTable, MockDiff,
    RefModulo, RefBoton, RefCampo,
};
export default ConfigMock;
